import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { Smartphone, ArrowUpRight } from "lucide-react";
import { RapidHero } from "./rapid/RapidHero";
import { RAPID_CATEGORIES, RAPID_BRANDS, RAPID_PERKS, RAPID_TRUST } from "../lib/rapidData";

const FADE = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.5 } };

function CategoryGrid() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="rapid-categories">
      {RAPID_CATEGORIES.map(({ icon: Icon, label }) => (
        <div key={label} className="bento-card rounded-lg border border-ib-steel/20 bg-ib-surface/50 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-ib-lime/10 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-ib-lime" /></div>
          <span className="font-outfit text-xs font-semibold text-ib-text">{label}</span>
        </div>
      ))}
    </div>
  );
}

function PerkList({ items, accent, testId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid={testId}>
      {items.map((p) => (
        <div key={p.title} className="glass rounded-xl p-5">
          <div className={`font-outfit font-bold text-sm ${accent}`}>{p.title}</div>
          <p className="font-manrope text-xs text-ib-text-secondary mt-1.5 leading-relaxed">{p.desc}</p>
        </div>
      ))}
    </div>
  );
}

export function RapidSection() {
  return (
    <section id="rapid" data-testid="rapid-section" className="py-24 px-6 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div {...FADE} className="mb-12 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 font-jetbrains text-xs text-ib-lime tracking-widest uppercase">
              <span data-testid="rapid-coming-soon-badge" className="px-1.5 py-0.5 rounded bg-ib-lime text-ib-bg text-[9px] font-bold">Coming Soon</span> IronByte Rapid
            </span>
            <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 text-ib-text">
              Building materials at your site, <span className="text-ib-lime glow-lime-text">fast</span>.
            </h2>
            <p className="font-manrope text-base md:text-lg text-ib-text-secondary mt-4 max-w-xl">
              Rapid is Iron Byte's quick-commerce arm for contractors and site engineers — small lots, cut to fit, with mill test certificates.
              Ordering a tonne or more? <Link to="/marketplace" data-testid="rapid-bulk-link" className="text-ib-blue hover:underline">Iron Byte Bulk</Link> has mill-direct rates.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[["60 min", "Delhi NCR delivery"], ["8am–8pm", "All days"], ["₹500+", "Free delivery"]].map(([v, l]) => (
                <div key={l}>
                  <div className="font-jetbrains text-2xl font-bold text-ib-text">{v}</div>
                  <div className="font-manrope text-[11px] text-ib-text-muted">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <RapidHero />
        </motion.div>

        <motion.div {...FADE}>
          <div className="font-jetbrains text-xs text-ib-text-muted uppercase tracking-widest mb-4">Shop by category</div>
          <CategoryGrid />
        </motion.div>

        <motion.div {...FADE} className="mt-10 glass rounded-xl p-6 flex flex-col sm:flex-row sm:items-center gap-4 border border-ib-lime/20" data-testid="rapid-coming-soon">
          <span className="self-start px-3 py-1 rounded-full bg-ib-lime text-ib-bg font-jetbrains text-[11px] font-bold uppercase tracking-widest">Coming Soon</span>
          <p className="font-manrope text-sm text-ib-text-secondary">IronByte Rapid is launching soon in Delhi NCR. Rapid prices will go live at launch.</p>
        </motion.div>

        <motion.div {...FADE} className="mt-10 rounded-xl border border-ib-steel/20 bg-ib-surface/30 py-4" data-testid="rapid-brands">
          <div className="font-jetbrains text-[10px] text-ib-text-muted uppercase tracking-widest px-6 mb-3">The brands your engineer and architect specify</div>
          <Marquee speed={30} gradient={false}>
            {RAPID_BRANDS.map((b) => <span key={b} className="mx-6 font-outfit font-bold text-sm text-ib-text-secondary whitespace-nowrap">{b}</span>)}
          </Marquee>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <motion.div {...FADE}>
            <h3 className="font-outfit font-bold text-xl text-ib-text">Built for contractors who buy every week.</h3>
            <p className="font-manrope text-sm text-ib-text-secondary mt-2 mb-5">Prices drop automatically as quantity goes up. Every order earns Rapid Cash you can use on the next one.</p>
            <PerkList items={RAPID_PERKS} accent="text-ib-lime" testId="rapid-perks" />
          </motion.div>
          <motion.div {...FADE}>
            <h3 className="font-outfit font-bold text-xl text-ib-text">Genuine, or your money back.</h3>
            <p className="font-manrope text-sm text-ib-text-secondary mt-2 mb-5">Every Rapid order is traceable to the mill or brand it came from.</p>
            <PerkList items={RAPID_TRUST} accent="text-ib-blue" testId="rapid-trust" />
            <div className="glass rounded-xl p-5 mt-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="rapid-app-cta">
              <div className="w-10 h-10 rounded-lg bg-ib-blue/15 flex items-center justify-center flex-shrink-0"><Smartphone className="w-5 h-5 text-ib-blue" /></div>
              <div className="flex-1">
                <div className="font-outfit font-bold text-sm text-ib-text">Get the Rapid app</div>
                <div className="font-manrope text-xs text-ib-text-secondary">Reorder in two taps, track your rider live, keep your Rapid Cash in one place.</div>
              </div>
              <div className="flex gap-2">
                {["Google Play", "App Store"].map((s) => (
                  <a key={s} href="#rapid" data-testid={`rapid-app-${s.toLowerCase().replace(/\s/g, "-")}`} className="inline-flex items-center gap-1 font-outfit text-xs px-3 py-2 rounded-lg border border-ib-steel/40 text-ib-text hover:border-ib-blue/50 transition-colors">
                    {s} <ArrowUpRight className="w-3 h-3" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
